import React, { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { Link, useHistory } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useAuthContext } from '../../../../../context/auth.context'
import { useApiFormOrganizationDeletion } from '../../../../../api-new/state/content'
import { useRoleOrgGroup, useRoleOrgNode, useRoleOrgOrganization, useRoleOrgUser } from '../../../../../role/hook/role-org'
import { serverMessage } from '../../../../../shared/error/server-message'
import { formatCount } from '../../../../../shared/sso-workspace/format'
import { FleetSection, Notice, RollupTiles } from '../../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { currentClaims, deletionDay, joinNames } from '../../../../../shared/organization-deletion/organization-deletion'
import { DeletionUnavailable } from './deletion-unavailable'
import { AIToolsSharing } from '../../../../../shared/ai-tools-sharing/ai-tools-sharing'
import { getOwnSetting, setOwnSetting } from '../../../../../shared/ai-tools-sharing/api'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { SECTION } from '../../../../../shared/navigation/sections'
import './organization.scss'

// The organization's own page (/org/organization; plan Task 26, redesigned
// 2026-10-07 in the Authentication page's workspace style): a header (the
// name, Company or Personal, when it was created), an overview (members,
// courses and groups, each a link where the viewer can open that page; the
// owners; the plan's features) and the danger zone.
//
// What the viewer can do follows their ACTUAL role in the organization
// (useRoleOrgOrganization; owner ruling 2026-10-07), even for a super admin:
// an owner gets Delete organization… (the typed-name confirmation, /delete)
// and Restore while it is scheduled; an admin is told only an owner can,
// and who the owners are; a personal organization is deleted with its
// owner's account; an impersonated session sees the person's page with the
// writes disabled and why (the server refuses them, ruling R8). The facts
// come from the deletion status's preflight (GET /organization-deletion).

// Why the viewer can't change the AI tools switch: an organization's owners
// change it (ORG_ORGANIZATION update); its admins see it; an impersonated
// session never writes (the server refuses it).
const aiToolsWhyNot = role => role.readOnly
  ? "You can't change this while you're viewing as someone else."
  : 'Only an owner can change this.'

// The plan's features, from the session's claims (the auth service puts
// the organization's plan on the token).
const FEATURES = [
  ['featureSSO', 'Single sign-on'],
  ['featureAnalytic', 'Analytics'],
  ['featureAssessment', 'Assessments'],
  ['featureMultiplayer', 'Multiplayer'],
  ['featureHeatmap', 'Heatmaps'],
  ['featureAR', 'AR'],
  ['featureAI', 'AI'],
  ['featureTradeShow', 'Trade show']
]

const createdOn = value => (value && !String(value).startsWith('0001-') ? moment(value).format('D MMM YYYY') : '')

const ownerLabel = owner => owner.name || owner.email

// One action in the danger zone: what it does, then its button.
const DangerRow = ({ title, children, action }) =>
  <div className="org-danger-row">
    <div className="org-danger-text">
      <strong>{title}</strong>
      {children}
    </div>
    {action && <div className="org-danger-action">{action}</div>}
  </div>

const Owners = ({ viewerOwns, owners, canOpen }) => {
  const people = owners.map((owner, i) =>
    <React.Fragment key={owner.userID || owner.email || i}>
      {i > 0 || viewerOwns ? ', ' : ''}
      {canOpen && owner.userID
        ? <Link to={`/org/user/list/edit/${owner.userID}`}>{ownerLabel(owner)}</Link>
        : ownerLabel(owner)}
    </React.Fragment>)
  if (!viewerOwns && owners.length === 0) return <span className="sw-hint">None listed</span>
  return (
    <span>
      {viewerOwns && 'You'}
      {people}
    </span>
  )
}

const DangerZone = ({ name, status, role, busy, onDelete, onDeleteAccount, onRestore }) => {
  const preflight = status.preflight
  const deletion = status.deletion
  const owners = (preflight.otherOwners || []).map(ownerLabel).filter(Boolean)
  const impersonated = !!role.readOnly
  const owner = role.accessDelete || role.accessUpdate || impersonated

  let body
  if (preflight.isPersonal) {
    body = (
      <DangerRow
        title={owner ? 'Deleted with your account' : "Deleted with its owner's account"}
        action={(role.accessDelete || impersonated) &&
          <button type="button" className="btn sw-btn-danger" disabled={impersonated} onClick={onDeleteAccount}>Delete account…</button>}>
        <p>A personal organization is deleted only with its owner's account, and everything in it goes with it.</p>
      </DangerRow>
    )
  } else if (deletion) {
    body = (
      <DangerRow
        title="Scheduled for deletion"
        action={(role.accessUpdate || impersonated) &&
          <button type="button" className="btn sw-btn" disabled={impersonated || busy} onClick={onRestore}>{busy ? 'Restoring…' : 'Restore'}</button>}>
        <p>{`${name} is scheduled for deletion on ${deletionDay(deletion.purgeAfter)}.`}</p>
        {!owner && <p>Only an owner can restore it.</p>}
      </DangerRow>
    )
  } else if (owner) {
    body = (
      <DangerRow
        title="Delete this organization"
        action={<button type="button" className="btn sw-btn-danger" disabled={impersonated} onClick={onDelete}>Delete organization…</button>}>
        <p>Members are signed out at once. Any owner can restore it for {preflight.restoreDays} days; after that it is permanently deleted.</p>
      </DangerRow>
    )
  } else {
    body = (
      <DangerRow title="Delete this organization">
        <p>Only an owner can delete this organization.</p>
        {owners.length > 0 && <p>{`Owners: ${joinNames(owners)}.`}</p>}
      </DangerRow>
    )
  }

  return (
    <section className="sw-section org-danger" id="danger" aria-labelledby="danger-title">
      <h2 className="h6 fw-semibold" id="danger-title">Danger zone</h2>
      {body}
      {impersonated && <DeletionUnavailable readOnly={role.readOnly} className="org-danger-why" />}
    </section>
  )
}

export const Organization = () => {
  const role = useRoleOrgOrganization()
  const roleUser = useRoleOrgUser()
  const roleNode = useRoleOrgNode()
  const roleGroup = useRoleOrgGroup()
  const history = useHistory()

  const authContext = useAuthContext()
  const state = (authContext && authContext.state) || {}
  const claims = currentClaims(state)
  const name = state.organizationName || claims.organizationName || ''

  // api-new hooks return a new object on every request-state change: read
  // it through a ref, never as an effect dependency.
  const apiFormOrganizationDeletion = useApiFormOrganizationDeletion()
  const apiRef = useRef(apiFormOrganizationDeletion)
  apiRef.current = apiFormOrganizationDeletion

  const [status, setStatus] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)

  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  const load = useCallback(
    () =>
      apiRef.current
        .get()
        .then(result => mounted.current && setStatus(result && result.preflight ? result : null))
        .catch(err => mounted.current && setError(serverMessage(err, "We couldn't load this organization's details. Try again in a moment."), err)),
    [setError]
  )

  useEffect(() => { load() }, [load])

  const handleRestore = async () => {
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      await apiRef.current.postArg('/restore', {})
      await load()
    } catch (err) {
      if (mounted.current) setError(serverMessage(err, `We couldn't restore ${name}. Try again in a moment.`), err)
    } finally {
      if (mounted.current) setBusy(false)
    }
  }

  const preflight = status && status.preflight
  const personal = preflight ? !!preflight.isPersonal : claims.organizationType === 'user'
  const created = preflight && createdOn(preflight.createdAt)
  const features = FEATURES.filter(([key]) => claims[key]).map(([, label]) => label)

  const tiles = preflight && [
    { key: 'members', label: 'Members', value: formatCount(preflight.members || 0), to: roleUser.accessRead ? '/org/user/list' : undefined },
    { key: 'courses', label: 'Courses', value: formatCount(preflight.courses || 0), to: roleNode.accessRead ? '/org/node-content' : undefined },
    { key: 'groups', label: 'Groups', value: formatCount(preflight.groups || 0), to: roleGroup.accessRead ? '/org/group/list' : undefined }
  ]

  return (
    <BreadcrumbView Icon={SECTION.organization.Icon} text={SECTION.organization.text}>
      <div className="sw-workspace sw-pane sw-fleet org-page" data-testid="organization-page">
        <header className="sw-page-head">
          <div className="min-w-0">
            <div className="org-page-title">
              <h1 className="sw-title">{name}</h1>
              {(preflight || claims.organizationType) && <Pill tone="info">{personal ? 'Personal' : 'Company'}</Pill>}
            </div>
            {created && <p className="sw-sub">{`Created ${created}`}</p>}
          </div>
        </header>

        {error &&
          <div className="mb-3">
            <Notice tone="bad" role="alert"><p>{error}</p></Notice>
            <ErrorReference error={failure} className="mt-2" />
          </div>}

        {!preflight && !error && <p className="sw-hint" role="status">Loading…</p>}

        {preflight &&
          <FleetSection id="overview" title="Overview">
            <RollupTiles tiles={tiles} label="What this organization holds" />
            <dl className="sw-dl org-facts">
              <dt>Owners</dt>
              <dd data-testid="organization-owners">
                <Owners viewerOwns={!!(role.accessDelete || role.readOnly)} owners={preflight.otherOwners || []} canOpen={!!roleUser.accessRead} />
              </dd>
              <dt>Plan features</dt>
              <dd data-testid="organization-features">
                {features.length > 0
                  ? <span className="org-features">{features.map(label => <Pill key={label} tone="ok">{label}</Pill>)}</span>
                  : <span className="sw-hint">None included</span>}
              </dd>
            </dl>
          </FleetSection>}

        <FleetSection id="ai-tools" title="AI tools">
          <AIToolsSharing load={getOwnSetting} save={setOwnSetting} canUpdate={!!role.accessUpdate} whyNot={aiToolsWhyNot(role)} />
        </FleetSection>

        {preflight &&
          <DangerZone
            name={name}
            status={status}
            role={role}
            busy={busy}
            onDelete={() => history.push('/org/organization/delete')}
            onDeleteAccount={() => history.push('/org/delete-account')}
            onRestore={handleRestore}
          />}
      </div>
    </BreadcrumbView>
  )
}
